/**
 * Building the results file, as a string.
 *
 * Pure, and separate from the download, because the two interesting decisions
 * here are both about text and neither is about the DOM.
 *
 * **A cell is never allowed to become a formula.** A promo code beginning `=`,
 * `+`, `-` or `@` is executed by Excel and by Google Sheets when the file is
 * opened — the classic CSV injection, and this file is opened by whoever does
 * the reconciliation, on a machine with the customer list on it. Prefixing with
 * an apostrophe is the fix that survives a round trip: the cell reads as text
 * and the value is still visible.
 *
 * **It starts with a byte-order mark.** Excel reads a BOM-less UTF-8 file as
 * the local single-byte codepage, which turns every Cyrillic name into mojibake
 * — and the panel's messages are Cyrillic. Three bytes, and the difference
 * between a file that works and a support call.
 */

export interface CsvColumn<Row> {
  readonly header: string;
  readonly value: (row: Row) => string | number | null | undefined;
}

/** Excel and LibreOffice both want CRLF; a lone LF is read as one long row by Excel. */
const LINE_END = '\r\n';

export const BOM = '﻿';

export function toCsv<Row>(rows: readonly Row[], columns: readonly CsvColumn<Row>[]): string {
  const head = columns.map((column) => csvCell(column.header)).join(',');
  const body = rows.map((row) => columns.map((column) => csvCell(column.value(row))).join(','));

  return BOM + [head, ...body].join(LINE_END) + LINE_END;
}

/**
 * One cell: neutralised, then quoted.
 *
 * Always quoted rather than only when it has to be. A conditional quote is one
 * more rule to get wrong, the file is read by a spreadsheet rather than by a
 * person, and the size difference on ten thousand rows is nothing.
 */
export function csvCell(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '""';

  const text = String(value);
  // The four leaders every spreadsheet treats as the start of a formula. Tab
  // and carriage return are here because a leading one is stripped by the
  // parser, exposing the character after it — so `\t=cmd` would still run.
  const neutral = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;

  return `"${neutral.replace(/"/g, '""')}"`;
}

/**
 * A name that sorts and does not collide.
 *
 * The campaign id first because that is what somebody looking for a particular
 * file has in their hand, and a date so two exports of the same campaign do not
 * silently overwrite one another in a downloads folder.
 */
export function csvFilename(campaignId: number, at: Date = new Date()): string {
  const stamp = [
    at.getFullYear(),
    String(at.getMonth() + 1).padStart(2, '0'),
    String(at.getDate()).padStart(2, '0'),
  ].join('-');

  return `campaign-${campaignId}-${stamp}.csv`;
}
